import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  Button,
  Input,
  message,
  Alert,
  Row,
  Col,
  List,
  Avatar,
  Skeleton,
  Divider,
  Card,
  notification,
} from "antd";
import Api from "api";
import styled from "styled-components";
import { ThemeConfig } from "theme";
import SearchInput from "components/Common/SearchInput";
const ACCEPT_UPDATE_STATE = "accept_update";
const KEEP_STATE = "keep_original";
const FIND_REPLACE_STATE = "find_replace";

const StyledCard = styled(Card)`
  .selectedState {
    border: 2px solid ${ThemeConfig.colors.darkBlue};
  }
  .autocompleteSearchInput {
    font-weight: 400;
  }
`;

const CollectionErrorReviewModal = ({ isOpen, onClose, collection }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorFixItems, setErrorFixItems] = useState<any>([]);
  const [autoSaving, setAutoSaving] = useState<boolean>(false);
  const autoSaveTimeoutRef = useRef<any>(null);
  const initialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    if (
      collection &&
      !collection.error_fixed &&
      collection.errorItems &&
      collection.errorItems.length > 0
    ) {
      // Check if there are saved partial fixes
      if (collection.partial_error_fixes && collection.partial_error_fixes.length > 0) {
        // Restore partial progress
        console.log('[RESTORE_PARTIAL] Restoring saved progress:', collection.partial_error_fixes.length, 'items');
        setErrorFixItems(collection.partial_error_fixes);
      } else {
        // Fresh start - initialize with defaults
        setErrorFixItems(
          collection.errorItems.map((_item) => ({
            item_id: _item._id,
            term_id: null,
            state: _item.document?.no_match ? KEEP_STATE : ACCEPT_UPDATE_STATE,
            selected_source: null,
            no_match: !!_item.document?.no_match,
            _item,
          }))
        );
      }
      initialLoadRef.current = false;
    }
  }, [collection]);

  // Auto-save function
  const autoSaveProgress = useCallback(async () => {
    if (!collection || !errorFixItems || errorFixItems.length === 0 || initialLoadRef.current) {
      return;
    }

    setAutoSaving(true);
    try {
      await Api.post(
        `/collections/${collection.id}/save_partial_error_items`,
        {
          items: errorFixItems,
        }
      );
      console.log('[AUTO_SAVE] Progress auto-saved successfully');
    } catch (e) {
      console.error('[AUTO_SAVE] Failed to auto-save:', e);
    } finally {
      setAutoSaving(false);
    }
  }, [collection, errorFixItems]);

  // Debounced auto-save when errorFixItems changes
  useEffect(() => {
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Don't auto-save on initial load
    if (initialLoadRef.current || !errorFixItems || errorFixItems.length === 0) {
      return;
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveProgress();
    }, 2000);

    // Cleanup on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [errorFixItems, autoSaveProgress]);

  const handleErrorStateChange = (item, state) => {
    const newErrorItems = errorFixItems.map((_item) => {
      if (_item.item_id === item.item_id) {
        _item.state = state;
      }
      return _item;
    });
    setErrorFixItems(newErrorItems);
  };

  const handleErrorStateSourceChange = (item, state, selected_source) => {
    if (!state || !selected_source) {
      return;
    }
    const newErrorItems = errorFixItems.map((_item) => {
      if (_item.item_id === item.item_id) {
        _item.state = state;
        _item.selected_source = selected_source;
      }
      return _item;
    });
    setErrorFixItems(newErrorItems);
  };
  
  const validateErrorItems = () => {
    let valid = true;
    errorFixItems.map((item) => {
      if (
        item.state == "" ||
        (item.state == FIND_REPLACE_STATE && !item.term_id)
      ) {
        valid = false;
      }
    });
    if (!valid) {
      message.error({
        content:
          "There are some errors in your items. Please check all items are properly set",
        duration: 2,
      });
    }
    return valid;
  };

  const onSave = async (values) => {
    setLoading(true);
    if (!validateErrorItems()) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await Api.post(
        `/collections/${collection.id}/fix_error_items`,
        {
          items: errorFixItems,
        }
      );
      setLoading(false);
      if (data && data.messages) {
        notification.success({
          message: "Error Fix Success",
          description: data.messages[0],
        });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to fix error",
        });
      }
      onClose('refetch');
    } catch (e: any) {
      console.log("error", e.response.data.messages[0]);
      setLoading(false);
      const errorMsg =
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.messages &&
          e.response.data.messages[0]) ||
        "Failed to map columns";
      notification.error({
        message: "Failed",
        description: errorMsg,
      });
      // setErr(errorMsg);
      // setTimeout(() => {
      //   setErr("");
      // }, 3000);
    }
  };

  const onDiscard = async (values) => {
    setLoading(true);
    // return;
    try {
      const { data } = await Api.post(
        `/collections/${collection.id}/discard_error_items`,
        {
          items: errorFixItems,
        }
      );
      console.log('discard res', data);
      setLoading(false);
      if (data) {
        notification.success({
          message: "Success",
          description: "Error Discarded",
        });
        onClose("refetch");
        // history.push(`/collections/${data.data.id}`);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to discard error",
        });
      }
    } catch (e: any) {
      console.log("error", e.response.data.messages[0]);
      setLoading(false);
      const errorMsg =
        (e &&
          e.response &&
          e.response.data &&
          e.response.data.messages &&
          e.response.data.messages[0]) ||
        "Failed to map columns";
      notification.error({
        message: "Failed",
        description: errorMsg,
      });
    }
  };
  const capitalize = (string) => {
    if(!string) {
      return string;
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  const handleSelectReplace = (item, term_id) => {
      const newErrorItems = errorFixItems.map((_item) => {
        if (_item.item_id === item.item_id) {
          _item.term_id = term_id;
        }
        return _item;
      });
      setErrorFixItems(newErrorItems);
  }
  const renderSingleErrorItem = (singleItem, rowIndex) => {
    return (
      // <List.Item>
      <StyledCard
        style={{ margin: "10px 5px" }}
        bodyStyle={{ padding: 10 }}
        key={rowIndex}
      >
        <Row>
          <span style={{ textAlign: "left", fontSize: 14, fontWeight: 400 }}>
            {!singleItem._item.document.no_match ? (
              <span>
                Record {rowIndex + 1} of {collection.errorItems.length}:{" "}
                {capitalize(
                  singleItem._item.document[`${collection.type}_name`]
                )}{" "}
                was updated to{" "}
                {capitalize(
                  singleItem._item.document.new_name
                    ? singleItem._item.document.new_name + "(new name)"
                    : singleItem._item.document.exact_name
                    ? singleItem._item.document.exact_name + "(exact name)"
                    : singleItem._item.document.closest_name + "(closest match)"
                )}
              </span>
            ) : (
              <span>
                Record {rowIndex + 1} of {collection.errorItems.length}:{" "}
                {capitalize(
                  singleItem._item.document[`${collection.type}_name`]
                )}{" "}
                does not match any data
              </span>
            )}
          </span>
        </Row>
        {singleItem._item?.document?.description && (
          <Row>
            <span style={{ textAlign: "left", fontSize: 14, fontWeight: 400, color: "#808080" }}> {singleItem._item.document.description}</span>
          </Row>
        )}
        {!singleItem._item.document.exact_name && (
          <Row>
            <Col style={{ padding: 5 }}>
              <Button
                // className="selectedState"
                type={singleItem.state === ACCEPT_UPDATE_STATE ? "primary" : "dashed"}
                className={
                  singleItem.state === ACCEPT_UPDATE_STATE
                    ? "selectedState"
                    : ""
                }
                // type="primary"
                style={{
                  fontSize: 12,
                  width: "100%",
                  color: singleItem.state === ACCEPT_UPDATE_STATE ? "#fff" : undefined
                }}
                disabled={singleItem._item.document.no_match}
                onClick={() => {
                  handleErrorStateChange(singleItem, ACCEPT_UPDATE_STATE);
                }}
              >
                ACCEPT UPDATE
              </Button>
            </Col>
            {singleItem._item.document.alternate_names && singleItem._item.document.alternate_names.length > 0 && singleItem._item.document.alternate_names.map((altNameObj) => (
              <Col style={{ padding: 5 }}>
                <Button
                  type={singleItem.state === "accept_different_source" && singleItem?.selected_source === altNameObj.source ? "primary" : "dashed"}
                  className={
                    singleItem.state === "accept_different_source" && singleItem?.selected_source === altNameObj.source
                      ? "selectedState"
                      : ""
                  }
                  style={{
                    fontSize: 12,
                    width: "100%",
                    color: (singleItem.state === "accept_different_source" && singleItem?.selected_source === altNameObj.source) ? "#fff" : undefined
                  }}
                  disabled={singleItem._item.document.no_match}
                  onClick={() => {
                    // handleErrorStateChange(singleItem, altNameObj.source);
                    handleErrorStateSourceChange(singleItem, 'accept_different_source' ,altNameObj.source);
                  }}
                >
                  ACCEPT {altNameObj.name} from source {altNameObj.source}
                </Button>
              </Col>
            ))}
            <Col style={{ padding: 5 }}>
              <Button
                type={singleItem.state === KEEP_STATE ? "primary" : "dashed"}
                className={
                  singleItem.state === KEEP_STATE ? "selectedState" : ""
                }
                style={{
                  width: 250,
                  fontSize: 12,
                  color: singleItem.state === KEEP_STATE ? "#fff" : undefined
                }}
                onClick={() => {
                  handleErrorStateChange(singleItem, KEEP_STATE);
                }}
              >
                KEEP {singleItem._item.document[`${collection.type}_name`]}
              </Button>
            </Col>
            <Col style={{ padding: 5 }}>
              <Row gutter={4}>
                <Col xs={12}>
                  <Button
                    type={singleItem.state === FIND_REPLACE_STATE ? "primary" : "dashed"}
                    className={
                      singleItem.state === FIND_REPLACE_STATE
                        ? "selectedState"
                        : ""
                    }
                    style={{
                      fontSize: 12,
                      width: "100%",
                      color: singleItem.state === FIND_REPLACE_STATE ? "#fff" : undefined
                    }}
                    disabled={singleItem._item.document.no_match}
                    onClick={() => {
                      handleErrorStateChange(singleItem, FIND_REPLACE_STATE);
                    }}
                  >
                    FIND & REPLACE
                  </Button>
                </Col>
                <Col xs={12} style={{ width: 200 }}>
                  <SearchInput
                    type={collection.type}
                    collectionId={collection.id}
                    placeholder="Search..."
                    onSelect={(term_id) => {
                      handleSelectReplace(singleItem, term_id);
                      handleErrorStateChange(singleItem, FIND_REPLACE_STATE);
                    }}
                    searchKey="all"
                    // database={database}
                    excludeIds={[]}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </StyledCard>
      // </List.Item>
    );
  };
  if (!collection) {
    return null;
  }
  
  const handleClose = () => {
    Modal.confirm({
      title: "Are you sure you want to exit?",
      content: "You can continue matching later. Your progress will be saved.",
      okText: "Continue Matching Later",
      cancelText: "Stay Here",
      onOk: async () => {
        // Save partial progress
        setLoading(true);
        try {
          const { data } = await Api.post(
            `/collections/${collection.id}/save_partial_error_items`,
            {
              items: errorFixItems,
            }
          );
          setLoading(false);
          if (data && data.success) {
            notification.success({
              message: "Progress Saved",
              description: "Your matching progress has been saved. You can continue later.",
            });
            onClose("cancel");
          } else {
            notification.error({
              message: "Save Failed",
              description: "Failed to save progress. Please try again.",
            });
          }
        } catch (e: any) {
          console.error("Error saving partial progress:", e);
          setLoading(false);
          const errorMsg =
            (e &&
              e.response &&
              e.response.data &&
              e.response.data.messages &&
              e.response.data.messages[0]) ||
            "Failed to save progress";
          notification.error({
            message: "Save Failed",
            description: errorMsg,
          });
        }
      },
    });
  };

  return (
    <Modal
      title="Updated Results:"
      style={{ textAlign: "center", fontWeight: "bold" }}
      bodyStyle={{
        textAlign: "left",
        padding: 0,
        maxHeight: "calc(100vh - 150px)",
        overflowY: "auto",
        // padding: "10px 24px",
      }}
      visible={isOpen}
      width={960}
      maskClosable={false}
      onCancel={handleClose}
      footer={[
        autoSaving && (
          <span key="autosave" style={{ float: 'left', color: '#1890ff', fontSize: 12, marginTop: 8 }}>
            Auto-saving...
          </span>
        ),
        <Button loading={loading} key="submit" type="primary" onClick={onSave}>
          Accept All
        </Button>,
        <Button key="review_later" onClick={handleClose}>
          Review Later
        </Button>,
        <Button key="back" onClick={onDiscard}>
          Discard
        </Button>,
      ]}
    >
      {/* <List dataSource={errorFixItems} renderItem={renderSingleErrorItem} /> */}
      {errorFixItems.map(renderSingleErrorItem)}
    </Modal>
  );
};
export default CollectionErrorReviewModal;
